/**
 * LastReadCard — Visual feedback for the last barcode scan result.
 * Shows success (green), error (red), or waiting (yellow pulsing) states.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Clock } from 'lucide-react';

export type LastReadStatus = 'success' | 'error' | 'waiting';

export interface LastReadInfo {
  status: LastReadStatus;
  message: string;
  produtoNome?: string | null;
  codigoProduto?: string | null;
  lote?: string | null;
  validade?: string | null;
  quantidade?: number;
  /** Countdown seconds remaining for waiting state */
  countdownSeconds?: number;
}

interface Props {
  info: LastReadInfo | null;
  onDismiss?: () => void;
}

export const LastReadCard: React.FC<Props> = ({ info, onDismiss }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!info) {
      setVisible(false);
      return;
    }
    setVisible(true);

    // Auto-dismiss: success=2s, error=5s (equivalente ao offline)
    if (info.status !== 'waiting') {
      const dismissMs = info.status === 'success' ? 2000 : 5000;
      const timer = setTimeout(() => {
        setVisible(false);
        onDismiss?.();
      }, dismissMs);
      return () => clearTimeout(timer);
    }
  }, [info, onDismiss]);

  if (!info || !visible) return null;

  const styles: Record<LastReadStatus, { bg: string; border: string; text: string; icon: React.ReactNode }> = {
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-300 dark:border-green-700',
      text: 'text-green-800 dark:text-green-300',
      icon: <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />,
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-300 dark:border-red-700',
      text: 'text-red-800 dark:text-red-300',
      icon: <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />,
    },
    waiting: {
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-300 dark:border-amber-700',
      text: 'text-amber-800 dark:text-amber-300',
      icon: <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 animate-pulse" />,
    },
  };

  const s = styles[info.status];

  return (
    <div className={`rounded-lg border ${s.border} ${s.bg} p-3 ${info.status === 'waiting' ? 'animate-pulse' : ''}`}>
      <div className="flex items-start gap-3">
        {s.icon}
        <div className="flex-1 min-w-0">
          <div className={`font-medium text-sm ${s.text}`}>
            {info.codigoProduto && (
              <span className="font-bold">{info.codigoProduto}</span>
            )}
            {info.codigoProduto && info.produtoNome && ' — '}
            {info.produtoNome && (
              <span>{info.produtoNome}</span>
            )}
            {!info.codigoProduto && !info.produtoNome && (
              <span>{info.message}</span>
            )}
          </div>
          <div className={`text-xs ${s.text} opacity-80 mt-0.5 flex flex-wrap gap-x-3`}>
            {info.lote && <span>Lote: {info.lote}</span>}
            {info.validade && <span>Val: {info.validade}</span>}
            {info.quantidade != null && info.status === 'success' && (
              <span className="font-semibold">Contagem: {info.quantidade}</span>
            )}
            {info.countdownSeconds != null && info.status === 'waiting' && (
              <span>{info.countdownSeconds}s restantes</span>
            )}
          </div>
          {(info.codigoProduto || info.produtoNome) && info.status !== 'success' && (
            <div className={`text-xs ${s.text} opacity-70 mt-0.5`}>{info.message}</div>
          )}
        </div>
      </div>
    </div>
  );
};
